import {
  useInfiniteQuery,
  QueryFunctionContext,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import FeedService from '../../services/feed';
import useFeatureFlag from '../use-feature-flag';
import { Feature } from '../../models/features/features';
import { Content } from '../../models/content/types';
import { contentOperations } from '../../models/content';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';

//this interface is in desperate need of a refactor to separate out the use cases of the different feed endpoints. (search, homepage, regular feed, profile feeds).
//I made an attempt, but I think it will also require API changes to make it clean. I want to avoid having many type definitions in various stages of deprecation for an api interface.

/**
 * Interface for content feed options
 */
export interface ContentFeedOptions {
  query?: string;
  filter?: string;
  channelId?: string;
  advocateId?: string;
  advocateFilter?: string;
  perPage?: number;
  includeTypes?: string[];
  initiativeId?: string;
  pinned?: boolean;
  queryEnabled?: boolean;
  homepageModuleParams?: HomepageModuleParams; //these are only functional if query is undefined (as that will hit the /search endpoint which does not support them, same with many of the other params here.)
}

export interface HomepageModuleParams {
  homepage_content_channel_ids?: number[];
  homepage_initiative_ids?: number[];
  only_followed_channels?: boolean;
  content_type?: 'admin-generated' | 'user-generated';
}

/**
 * Interface for content feed response metadata
 */
export interface ContentFeedMeta {
  page: {
    number: number;
    size: number;
  };
  total: number;
  total_pages: number;
}

/**
 * Interface for content feed response
 */
export interface ContentFeedResponse {
  feed?: Array<{ attributes: Content }>;
  contents?: Array<{ attributes: Content }>;
  meta: ContentFeedMeta;
}

/**
 * Interface for paginated content feed data
 */
export interface ContentFeedPage {
  items: Content[];
  nextPage: number | null;
  meta: ContentFeedMeta;
}

/**
 * Base query key for content feed
 */
export const CONTENT_FEED_QUERY_KEY = ['contentFeed'] as const;

/**
 * Generates a consistent query key based on all the feed options
 *
 * @param options - Options for the content feed and feature flags
 * @returns An array to be used as the query key
 */
const queryKey = ({
  query,
  filter,
  channelId,
  advocateId,
  advocateFilter,
  perPage,
  includeTypes,
  initiativeId,
  pinned,
  tagUserEnabled,
  advancedSearchEnabled,
  homepageModuleParams,
}: ContentFeedOptions & {
  tagUserEnabled: boolean;
  advancedSearchEnabled: boolean;
}): string[] => {
  return [
    CONTENT_FEED_QUERY_KEY[0],
    query || '',
    filter || '',
    channelId || '',
    advocateId || '',
    advocateFilter || '',
    perPage?.toString() || '',
    includeTypes?.join(',') || '',
    initiativeId || '',
    pinned ? 'pinned' : '',
    tagUserEnabled ? 'tagEnabled' : 'tagDisabled',
    advancedSearchEnabled ? 'advancedSearch' : 'basicSearch',
    homepageModuleParams ? JSON.stringify(homepageModuleParams) : '',
  ];
};

/**
 * Custom hook to fetch infinite content feed with pagination support
 *
 * This hook uses React Query's infinite query capabilities to fetch paginated content feed data.
 * It automatically manages the pagination state and provides methods to fetch more data.
 *
 * It also maintains compatibility with the existing Redux store by dispatching content items.
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   isLoading,
 *   isError,
 *   error
 * } = useContentFeedQuery({ channelId: "123" });
 *
 * // Access all feed items as a flat array
 * const allItems = flattenContentFeedPages(data?.pages);
 *
 * // Load more items when needed
 * const loadMore = () => {
 *   if (hasNextPage && !isFetchingNextPage) {
 *     fetchNextPage();
 *   }
 * };
 * ```
 *
 * @param options - Options for the content feed
 * @param queryOptions - Optional React Query options
 * @returns Infinite query result with content feed data and methods to fetch more
 */
export const useContentFeedQuery = (
  {
    query,
    filter,
    channelId,
    advocateId,
    advocateFilter,
    perPage = 20,
    includeTypes,
    initiativeId,
    pinned,
    homepageModuleParams,
    queryEnabled = true,
  }: ContentFeedOptions = {},
  queryOptions?: Omit<
    UseInfiniteQueryOptions<
      ContentFeedPage,
      Error,
      ContentFeedPage,
      ContentFeedPage,
      string[]
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >
) => {
  const tagUserEnabled = useFeatureFlag(Feature.TAG_USER_ENABLED);
  const advancedSearchEnabled = useFeatureFlag(Feature.IMPROVED_SEARCH);
  const dispatch = useDispatch();
  const queryObject = useInfiniteQuery<
    ContentFeedPage,
    Error,
    ContentFeedPage,
    string[]
  >({
    queryKey: queryKey({
      query,
      filter,
      channelId,
      advocateId,
      advocateFilter,
      perPage,
      includeTypes,
      initiativeId,
      pinned,
      tagUserEnabled: !!tagUserEnabled,
      advancedSearchEnabled: !!advancedSearchEnabled,
      homepageModuleParams,
    }),
    enabled: queryEnabled,
    queryFn: async ({
      pageParam = 1,
    }: QueryFunctionContext<string[], number>) => {
      const feedService = new FeedService({
        query,
        filter,
        channelId,
        advocateId,
        advocateFilter,
        includeTypes,
        initiativeId,
        mention_format: tagUserEnabled ? 'with_metadata' : undefined,
        useLegacyMetadata: !advancedSearchEnabled,
        homepageModuleParams,
      });

      const response = await feedService.fetch({
        page: pageParam,
        perPage,
        pinned,
      });

      const items = (response.feed || response.contents)
        .map((fi) => fi.attributes)
        .filter((fi) =>
          Object.prototype.hasOwnProperty.call(fi, 'content_type')
        );

      if (perPage && items.length > perPage) {
        items.splice(perPage);
      }

      const meta = response.meta;
      const nextPage = pageParam < meta.total_pages ? pageParam + 1 : null;

      return {
        items,
        nextPage,
        meta,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    ...queryOptions,
  });

  // Dispatch contents to Redux store
  // This is a temporary solution to ensure the contents are available in the Redux store
  // This should be removed after migrating from Redux to React Query

  // Note: There are scenarios where the queryFn may have not been called but queryObject.data has
  // data from cache ( local storage) so it is important that the dispatch is done outside of the queryFn
  useEffect(() => {
    if (queryObject.data) {
      dispatch(
        contentOperations.addContents(
          queryObject.data.pages.flatMap((page) => page.items)
        )
      );
    }
  }, [queryObject.data, dispatch]);

  return {
    ...queryObject,
    rawQueryData: queryObject.data,
    data: queryObject.data?.pages.flatMap((page) => page.items),
  };
};
